// src/components/support/ContributionOptionsSection.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ContentCard } from '@dopomogai/ui';
import { renderIcon } from '@dopomogai/utils';
import { IconButton } from '@dopomogai/ui';
import { Linkedin, Twitter } from 'lucide-react';

interface ContributionOptionsSectionProps {
  onAction: (actionType: string, payload?: any) => void;
}

export const ContributionOptionsSection: React.FC<ContributionOptionsSectionProps> = ({ onAction }) => {
  const { t } = useTranslation('support_us');

  return (
    <section id="contribution-options" className="bg-slate-50 py-16 md:py-24">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">{t('contributionOptions.headline')}</h2>
        </motion.div>
        
        {/* Using a wrapper div to center the grid content itself */}
        <div className="flex justify-center">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
              <ContentCard
                index={0}
                icon={renderIcon('gift', { size: 28, className: 'text-gray-800' })}
                iconContainerClassName="p-3 bg-slate-100 border-2 border-black rounded-lg w-fit"
                title={t('contributionOptions.general.title')}
                text={t('contributionOptions.general.text')}
                cta={{
                  text: t('contributionOptions.general.cta'),
                  onClick: () => onAction('OPEN_URL', { url: 'YOUR_DONATION_LINK_HERE', newTab: true }),
                  variant: 'primary',
                }}
              />
              <ContentCard
                index={1}
                icon={renderIcon('target', { size: 28, className: 'text-gray-800' })}
                iconContainerClassName="p-3 bg-slate-100 border-2 border-black rounded-lg w-fit"
                title={t('contributionOptions.impact.title')}
                text={t('contributionOptions.impact.text')}
                cta={{
                  text: t('contributionOptions.impact.cta'),
                  onClick: () => onAction('NAVIGATE_TO_PAGE', { path: '/impact' }),
                }}
              />
              <ContentCard
                index={2}
                icon={renderIcon('briefcase', { size: 28, className: 'text-gray-800' })}
                iconContainerClassName="p-3 bg-slate-100 border-2 border-black rounded-lg w-fit"
                title={t('contributionOptions.partner.title')}
                text={t('contributionOptions.partner.text')}
                cta={{
                  text: t('contributionOptions.partner.cta'),
                  onClick: () => onAction('NAVIGATE_TO_PAGE', { path: '/contact?subject=PartnershipInquiry' }),
                }}
              />
              <ContentCard
                index={3}
                icon={renderIcon('megaphone', { size: 28, className: 'text-gray-800' })}
                iconContainerClassName="p-3 bg-slate-100 border-2 border-black rounded-lg w-fit"
                title={t('contributionOptions.amplify.title')}
                text={
                  <div className="flex-grow flex flex-col justify-between">
                    <p>{t('contributionOptions.amplify.text')}</p>
                    <div className="mt-6 pt-6 border-t-2 border-slate-200">
                      <div className="flex items-center justify-center gap-4">
                        <IconButton
                          icon={<Linkedin />}
                          ariaLabelKey="contributionOptions.amplify.ariaLabel.linkedin"
                          onClick={() => onAction('OPEN_URL', { url: 'YOUR_LINKEDIN_URL', newTab: true })}
                          variant="secondary"
                        />
                        <IconButton
                          icon={<Twitter />}
                          ariaLabelKey="contributionOptions.amplify.ariaLabel.twitter"
                          onClick={() => onAction('OPEN_URL', { url: 'YOUR_TWITTER_URL', newTab: true })}
                          variant="secondary"
                        />
                      </div>
                    </div>
                  </div>
                }
              />
              {/* This card will now flow correctly into the grid. */}
              <ContentCard
                index={4}
                className="md:col-span-2 lg:col-span-1" // On medium screens, it spans two columns to center itself. On large, it's just one item.
                icon={renderIcon('rocket', { size: 28, className: 'text-indigo-600' })}
                iconContainerClassName="p-3 bg-indigo-100 border-2 border-black rounded-lg w-fit"
                title={t('contributionOptions.joinTeam.title')}
                text={t('contributionOptions.joinTeam.text')}
                cta={{
                  text: t('contributionOptions.joinTeam.cta'),
                  onClick: () => onAction('NAVIGATE_TO_PAGE', { path: '/careers' }),
                  variant: 'secondary'
                }}
              />
            </div>
        </div>
      </div>
    </section>
  );
};

